"use client"

import { Loader2 } from "lucide-react"
import React from "react"
import { DropdownMenuPortal, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger } from "../shadcn/dropdown-menu"

/**
 * @description 하위 메뉴가 있는 메뉴 아이템. hover 시 서브메뉴 표시
 * @example
 * <TNestedMenuItem label="정렬" prefix={<TIcon name="sort" size={16} />}>
 *   <TMenuItem label="이름순" />
 *   <TMenuItem label="날짜순" />
 * </TNestedMenuItem>
 */
interface TNestedMenuItemProps {
	/** 메뉴 항목 텍스트 또는 커스텀 노드 */
	label: string | React.ReactNode
	/** 아이콘 등 앞부분 요소 */
	prefix?: React.ReactNode
	/** 비활성화 */
	disabled?: boolean
	/** 로딩 상태 (스피너 표시) */
	loading?: boolean
	/** 추가 CSS 클래스 */
	className?: string
	/** 서브메뉴 컨테이너 CSS 클래스 */
	subMenuClassName?: string
	/** 서브메뉴 내용 (TMenuItem 등) */
	children: React.ReactNode
	/** 제어 모드용 서브메뉴 열림 상태 */
	open?: boolean
	/** 서브메뉴 열림 상태 변경 */
	onOpenChange?: (open: boolean) => void
}

export const TNestedMenuItem = React.memo((props: TNestedMenuItemProps) => {
	const { label, prefix, disabled, loading, className, subMenuClassName, children, open, onOpenChange } = props
	const [isOpen, setIsOpen] = React.useState(false)
	const closeTimerRef = React.useRef<NodeJS.Timeout | undefined>(undefined)
	const handleOpenChange = React.useCallback(
		(nextOpen: boolean) => {
			if (open === undefined) setIsOpen(nextOpen)
			onOpenChange?.(nextOpen)
		},
		[open, onOpenChange]
	)

	const handlePointerMove = React.useCallback(() => {
		if (closeTimerRef.current) {
			clearTimeout(closeTimerRef.current)
		}
	}, [])

	const handlePointerLeave = React.useCallback(() => {
		closeTimerRef.current = setTimeout(() => {
			handleOpenChange(false)
		}, 150)
	}, [handleOpenChange])

	React.useEffect(() => {
		return () => {
			if (closeTimerRef.current) {
				clearTimeout(closeTimerRef.current)
			}
		}
	}, [])

	return (
		<DropdownMenuSub open={open ?? isOpen} onOpenChange={handleOpenChange}>
			<DropdownMenuSubTrigger disabled={disabled} data-loading={loading} className={className} onPointerMove={handlePointerMove} onPointerLeave={handlePointerLeave}>
				{loading && (
					<span className="loader">
						<Loader2 aria-hidden="true" />
					</span>
				)}
				{prefix && !loading && prefix}
				<span>{label}</span>
			</DropdownMenuSubTrigger>
			<DropdownMenuPortal>
				<DropdownMenuSubContent sideOffset={8} alignOffset={-4} className={subMenuClassName} onPointerMove={handlePointerMove} onPointerLeave={handlePointerLeave}>
					{children}
				</DropdownMenuSubContent>
			</DropdownMenuPortal>
		</DropdownMenuSub>
	)
})

TNestedMenuItem.displayName = "TNestedMenuItem"
